import { Resume } from "lib/redux/types";
import validator from "validator";

/**
 * @param {string} input - The input string to sanitize.
 * @returns {string} - The sanitized and normalized string.
 */
export function sanitizeString(input: string): string {
  if (typeof input !== "string") {
    throw new TypeError("Input must be a string");
  }

  // Step 1: Remove null characters (\u0000) and escaped nulls (\\u0000)
  let sanitized = input.replace(/\\u0000|\u0000/g, "");

  // Step 2: Remove non-printable characters (control characters, etc.)
  sanitized = sanitized.replace(
    /[\u0000-\u001F\u007F\u200B\u200C\u200D\uFEFF]/g,
    ""
  );

  // Step 3: Normalize the string to avoid unexpected Unicode issues (NFKC normalization)
  sanitized = sanitized.normalize("NFKC");

  // Step 4: Trim leading and trailing whitespace
  sanitized = sanitized.trim();

  return sanitized;
}

function sanitizeStringArray(arr: string[]): string[] {
  if (!Array.isArray(arr)) return [];
  return arr.map((str) => sanitizeString(str)).filter(Boolean);
}

function sanitizeUrl(url: string): string {
  if (!url) return "";
  const cleaned = sanitizeString(url);
  return validator.isURL(cleaned) ? cleaned : "";
}

function sanitizeEmail(email: string): string {
  if (!email) return "";
  const cleaned = sanitizeString(email);
  return validator.isEmail(cleaned) ? cleaned : "";
}

function sanitizePhone(phone: string): string {
  if (!phone) return "";
  const cleaned = sanitizeString(phone);
  // Remove all non-numeric characters except + (for international numbers)
  return cleaned.replace(/[^\d+]/g, "");
}

function sanitizeDate(date: string): string {
  if (!date) return "";
  const cleaned = sanitizeString(date);
  return cleaned;
}

function sanitizeGPA(gpa: string): string {
  if (!gpa) return "";
  const cleaned = sanitizeString(gpa);
  return cleaned.replace(/[^\d.%]/g, "");
}

/* 
We are not sanitizing the descriptions as they are html and we want to keep the formatting as is for the user and avoid any issues with sanitizing html tags
*/

export function sanitizeResume(resume: Partial<Resume>) {
  const sanitizedResume = { ...resume };

  // Sanitize Profile
  if (sanitizedResume?.profile) {
    sanitizedResume.profile = {
      ...sanitizedResume.profile,
      name: sanitizeString(sanitizedResume.profile?.name ?? ""),
      email: sanitizeEmail(sanitizedResume.profile?.email ?? ""),
      phone: sanitizeString(sanitizedResume.profile?.phone ?? ""),
      url1: sanitizeString(sanitizedResume.profile.url1),
      url2: sanitizeString(sanitizedResume.profile?.url2 ?? ""),
      summary: sanitizeString(sanitizedResume.profile?.summary ?? ""),
      location: sanitizeString(sanitizedResume.profile?.location ?? ""),
    };
  }

  // Sanitize Work Experience
  if (sanitizedResume.workExperiences) {
    sanitizedResume.workExperiences = sanitizedResume.workExperiences.map(
      (work) => ({
        ...work,
        company: sanitizeString(work?.company),
        jobTitle: sanitizeString(work?.jobTitle),
        startDate: sanitizeDate(work?.startDate),
        endDate: sanitizeDate(work?.endDate),
      })
    );
  }

  // Sanitize Education
  if (sanitizedResume.educations) {
    sanitizedResume.educations = sanitizedResume.educations.map((edu) => ({
      ...edu,
      school: sanitizeString(edu?.school),
      degree: sanitizeString(edu?.degree),
      startDate: sanitizeDate(edu?.startDate),
      endDate: sanitizeDate(edu?.endDate),
      gpa: sanitizeGPA(edu?.gpa),
    }));
  }

  // Sanitize Projects
  if (sanitizedResume.projects) {
    sanitizedResume.projects = sanitizedResume.projects.map((proj) => ({
      ...proj,
      project: sanitizeString(proj?.project),
      startDate: sanitizeDate(proj?.startDate),
      endDate: sanitizeDate(proj?.endDate),
      projectLink: sanitizeUrl(proj?.projectLink),
    }));
  }

  // Sanitize Skills
  // Description Only so no need to sanitize

  // Sanitize Certificates
  if (sanitizedResume.certifications) {
    sanitizedResume.certifications = sanitizedResume.certifications.map(
      (cert) => ({
        ...cert,
        certificate: sanitizeString(cert?.certificate),
        certificateLink: sanitizeUrl(cert?.certificateLink),
      })
    );
  }

  // Sanitize Languages
  if (sanitizedResume.languages) {
    sanitizedResume.languages = sanitizedResume.languages.map((lang) => ({
      ...lang,
      language: sanitizeString(lang?.language),
      level: sanitizeString(lang?.level),
    }));
  }

  // Sanitize Awards
  if (sanitizedResume.awards) {
    sanitizedResume.awards = sanitizedResume.awards.map((award) => ({
      ...award,
      award: sanitizeString(award?.award),
      issuer: sanitizeString(award?.issuer),
    }));
  }

  // Sanitize Custom
  // Description Only so no need to sanitize

  return sanitizedResume;
}
